import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, User } from 'firebase/auth';
import { auth, googleProvider } from '../../firebase';
import styles from './ApproveAccess.module.css';

type LoanRole = 'lender' | 'borrower' | 'reader';
type LoanId = 'earnest' | 'nagu';

const LOANS: { id: LoanId; label: string }[] = [
  { id: 'earnest', label: 'Earnest' },
  { id: 'nagu', label: 'Nagu' },
];

type Status =
  | 'checkingAuth'
  | 'signedOut'
  | 'loading'
  | 'invalid'
  | 'ready'
  | 'submitting'
  | 'done'
  | 'submitError';

function summarizeGrant(superuser: boolean, loanRoles: Partial<Record<LoanId, LoanRole>>): string {
  if (superuser) return 'superuser';
  return Object.entries(loanRoles)
    .map(([loanId, role]) => `${role} on ${loanId}`)
    .join(', ');
}

const ApproveAccess: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const requestId = params.get('requestId') ?? '';
  const token = params.get('token') ?? '';

  const [status, setStatus] = useState<Status>('checkingAuth');
  const [user, setUser] = useState<User | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [superuser, setSuperuser] = useState(false);
  const [loanRoles, setLoanRoles] = useState<Partial<Record<LoanId, LoanRole>>>({});

  useEffect(() => {
    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setStatus(nextUser ? 'loading' : 'signedOut');
    });
  }, []);

  useEffect(() => {
    if (status !== 'loading' || !user) {
      return;
    }

    if (!requestId || !token) {
      setStatus('invalid');
      setErrorMessage('This link is missing required information.');
      return;
    }

    user
      .getIdToken()
      .then((idToken) =>
        fetch(`/api/approveAccess?requestId=${encodeURIComponent(requestId)}&token=${encodeURIComponent(token)}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) {
          setErrorMessage(body.error ?? 'This link is no longer valid.');
          setStatus('invalid');
          return;
        }
        setName(body.name ?? '');
        setEmail(body.email ?? '');
        setStatus('ready');
      })
      .catch(() => {
        setErrorMessage('Could not reach the server.');
        setStatus('invalid');
      });
  }, [status, user, requestId, token]);

  const handleSignIn = () => {
    signInWithPopup(auth, googleProvider).catch(() => {
      // User closed the popup or sign-in failed; state stays 'signedOut'.
    });
  };

  const updateLoanRole = (loanId: LoanId, value: string) => {
    setLoanRoles((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[loanId];
      } else {
        next[loanId] = value as LoanRole;
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setStatus('submitting');

    user
      .getIdToken()
      .then((idToken) =>
        fetch('/api/approveAccess', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ requestId, token, superuser, loanRoles }),
        })
      )
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) {
          setErrorMessage(body.error ?? 'Failed to grant access.');
          setStatus('submitError');
          return;
        }
        setStatus('done');
      })
      .catch(() => {
        setErrorMessage('Could not reach the server.');
        setStatus('submitError');
      });
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {(status === 'checkingAuth' || status === 'loading') && <p className={styles.subtitle}>Loading&hellip;</p>}

        {status === 'signedOut' && (
          <>
            <h1 className={styles.title}>Sign in required</h1>
            <p className={styles.subtitle}>Only a superuser can review and grant access requests.</p>
            <button className={styles.button} onClick={handleSignIn}>
              Sign in with Google
            </button>
          </>
        )}

        {(status === 'invalid' || status === 'submitError') && (
          <>
            <h1 className={styles.title}>{status === 'invalid' ? 'Link not valid' : 'Something went wrong'}</h1>
            <p className={styles.subtitle}>{errorMessage}</p>
          </>
        )}

        {status === 'done' && (
          <>
            <h1 className={styles.title}>Access granted</h1>
            <p className={styles.subtitle}>
              {email} was granted <strong>{summarizeGrant(superuser, loanRoles)}</strong>.
            </p>
          </>
        )}

        {(status === 'ready' || status === 'submitting') && (
          <>
            <h1 className={styles.title}>Access request</h1>
            <p className={styles.subtitle}>
              {name || '(no name provided)'} &middot; {email}
            </p>

            <form className={styles.form} onSubmit={handleSubmit}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={superuser} onChange={(e) => setSuperuser(e.target.checked)} />
                Superuser (full access to everything)
              </label>

              {!superuser &&
                LOANS.map((loan) => (
                  <label key={loan.id} className={styles.field}>
                    {loan.label}
                    <select
                      value={loanRoles[loan.id] ?? ''}
                      onChange={(e) => updateLoanRole(loan.id, e.target.value)}
                    >
                      <option value="">No access</option>
                      <option value="lender">Lender</option>
                      <option value="borrower">Borrower</option>
                      <option value="reader">Reader</option>
                    </select>
                  </label>
                ))}

              <button type="submit" className={styles.button} disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Granting…' : 'Grant Access'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ApproveAccess;
